import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const submitContact = (data) => axios.post(`${API}/contact`, data);
export const subscribeNewsletter = (data) => axios.post(`${API}/newsletter`, data);
export const requestLeadMagnet = (data) => axios.post(`${API}/lead-magnet`, data);
export const leadMagnetFileUrl = (slug) => `${API}/lead-magnet/${slug}/file`;
export const sampleReportUrl = `${API}/sample-report/file`;
export const submitAssessment = (data) => axios.post(`${API}/assessment`, data);
export const submitSnapshot = (data) => axios.post(`${API}/snapshot`, data);
export const submitItSupportLead = (data) => axios.post(`${API}/it-support-lead`, data);
export const trackSnapshotView = (variant) =>
  axios.post(`${API}/snapshot/view`, { variant }).catch(() => {});
export const getHeroVariantLock = () => axios.get(`${API}/snapshot/hero-variant`);
export const submitWebinarRegistration = (data) => axios.post(`${API}/webinar/register`, data);

const withCreds = { withCredentials: true };
export const adminApi = {
  login: (email, password) => axios.post(`${API}/auth/login`, { email, password }, withCreds),
  logout: () => axios.post(`${API}/auth/logout`, {}, withCreds),
  me: () => axios.get(`${API}/auth/me`, withCreds),
  refresh: () => axios.post(`${API}/auth/refresh`, {}, withCreds),
  stats: () => axios.get(`${API}/admin/stats`, withCreds),
  contacts: () => axios.get(`${API}/contact`, withCreds),
  leads: () => axios.get(`${API}/lead-magnet`, withCreds),
  assessments: () => axios.get(`${API}/assessment`, withCreds),
  snapshots: () => axios.get(`${API}/snapshot`, withCreds),
  setSnapshotStatus: (id, status) => axios.patch(`${API}/snapshot/${id}`, { status }, withCreds),
  setSnapshotNotes: (id, notes) => axios.patch(`${API}/snapshot/${id}`, { notes }, withCreds),
  snapshotFunnel: (range) => axios.get(`${API}/snapshot/funnel?range=${range}`, withCreds),
  setHeroLock: (variant) => axios.post(`${API}/snapshot/hero-lock`, { variant }, withCreds),
  generateSnapshotReport: (id, data) =>
    axios.post(`${API}/snapshot/${id}/report`, data, { ...withCreds, responseType: "blob" }),
  emailSnapshotReport: (id, data) => axios.post(`${API}/snapshot/${id}/report/email`, data, withCreds),
  itSupportLeads: () => axios.get(`${API}/it-support-lead`, withCreds),
  setItSupportStatus: (id, status) => axios.patch(`${API}/it-support-lead/${id}`, { status }, withCreds),
  deleteItSupportLead: (id) => axios.delete(`${API}/it-support-lead/${id}`, withCreds),
  newsletter: () => axios.get(`${API}/newsletter`, withCreds),
  webinars: () => axios.get(`${API}/webinar/registrations`, withCreds),
  deleteLead: (id) => axios.delete(`${API}/lead-magnet/${id}`, withCreds),
  deleteAssessment: (id) => axios.delete(`${API}/assessment/${id}`, withCreds),
  deleteSnapshot: (id) => axios.delete(`${API}/snapshot/${id}`, withCreds),
  deleteContact: (id) => axios.delete(`${API}/contact/${id}`, withCreds),
  deleteNewsletter: (id) => axios.delete(`${API}/newsletter/${id}`, withCreds),
  deleteWebinar: (id) => axios.delete(`${API}/webinar/registrations/${id}`, withCreds),
  bulkDelete: (section, ids) => axios.post(`${API}/admin/bulk-delete`, { section, ids }, withCreds),
  supportRequests: () => axios.get(`${API}/support-requests`, withCreds),
  updateSupportRequest: (id, data) => axios.patch(`${API}/support-requests/${id}`, data, withCreds),
  markSupportSeen: (id) => axios.post(`${API}/support-requests/${id}/seen`, {}, withCreds),
  addSupportAttachments: (id, files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    return axios.post(`${API}/support-requests/${id}/attachments`, fd, withCreds);
  },
  deleteSupportRequest: (id) => axios.delete(`${API}/support-requests/${id}`, withCreds),
  clients: () => axios.get(`${API}/admin/clients`, withCreds),
  createClient: (data) => axios.post(`${API}/admin/clients`, data, withCreds),
  setClientActive: (id, active) => axios.patch(`${API}/admin/clients/${id}`, { active }, withCreds),
  deleteClient: (id) => axios.delete(`${API}/admin/clients/${id}`, withCreds),
  engagements: () => axios.get(`${API}/admin/engagements`, withCreds),
  createEngagement: (data) => axios.post(`${API}/admin/engagements`, data, withCreds),
  updateEngagement: (id, data) => axios.patch(`${API}/admin/engagements/${id}`, data, withCreds),
  uploadDocument: (engagementId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/admin/engagements/${engagementId}/documents`, fd, withCreds);
  },
  sendDigest: () => axios.post(`${API}/admin/digest/send`, {}, withCreds),
};

export const portalApi = {
  engagements: () => axios.get(`${API}/portal/engagements`, withCreds),
  documentUrl: (id) => `${API}/portal/documents/${id}/download`,
  changePassword: (current_password, new_password) =>
    axios.post(`${API}/auth/change-password`, { current_password, new_password }, withCreds),
  messages: (engagementId) => axios.get(`${API}/portal/engagements/${engagementId}/messages`, withCreds),
  sendMessage: (engagementId, body) =>
    axios.post(`${API}/portal/engagements/${engagementId}/messages`, { body }, withCreds),
  unreadCounts: () => axios.get(`${API}/portal/unread-counts`, withCreds),
  markRead: (engagementId) =>
    axios.post(`${API}/portal/engagements/${engagementId}/messages/read`, {}, withCreds),
  supportRequests: () => axios.get(`${API}/portal/support-requests`, withCreds),
  createSupportRequest: ({ subject, priority, description, files = [] }) => {
    const fd = new FormData();
    fd.append("subject", subject);
    fd.append("priority", priority);
    fd.append("description", description);
    files.forEach((f) => fd.append("files", f));
    return axios.post(`${API}/portal/support-requests`, fd, withCreds);
  },
  supportAttachmentUrl: (reqId, attId) => `${API}/support-requests/${reqId}/attachments/${attId}`,
  markSupportSeen: (id) => axios.post(`${API}/support-requests/${id}/seen`, {}, withCreds),
  addSupportReply: (id, { body = "", files = [] }) => {
    const fd = new FormData();
    fd.append("body", body);
    files.forEach((f) => fd.append("files", f));
    return axios.post(`${API}/support-requests/${id}/replies`, fd, withCreds);
  },
};
